import React, { useState, useEffect, useRef, useCallback } from 'react';
import { memoryService, MemoryPlace } from '../../services/memoryService';
import './MemoryPalace.css';

interface ConnectionFormProps {
  userId: number;
  fromPlaceId: number;
  existingPlaces?: number[];
  places?: MemoryPlace[];
  onSave: () => void;
  onCancel: () => void;
}

const ConnectionForm: React.FC<ConnectionFormProps> = ({
  userId,
  fromPlaceId,
  existingPlaces = [],
  places: providedPlaces,
  onSave,
  onCancel
}) => {
  const [toPlaceId, setToPlaceId] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [availablePlaces, setAvailablePlaces] = useState<MemoryPlace[]>([]);
  const [loading, setLoading] = useState(!providedPlaces);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadAvailablePlaces = useCallback(async () => {
    try {
      setLoading(true);
      const response = await memoryService.getUserPlaces(userId, 100);
      if (response.success && response.places) {
        // Filter out the current place and already connected places
        const filtered = response.places.filter(
          place => !existingPlaces.includes(place.id)
        );
        // Sort places by name
        const sorted = filtered.sort((a, b) => 
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
        setAvailablePlaces(sorted);
      }
    } catch (err) {
      console.error('Error loading places:', err);
      setError('Failed to load places');
    } finally {
      setLoading(false);
    }
  }, [userId, existingPlaces]);

  useEffect(() => {
    if (providedPlaces) {
      // Use provided places, filter and sort them
      const filtered = providedPlaces.filter(
        place => !existingPlaces.includes(place.id)
      );
      const sorted = filtered.sort((a, b) => 
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
      setAvailablePlaces(sorted);
      setLoading(false);
    } else if (userId) {
      loadAvailablePlaces();
    }
  }, [userId, providedPlaces, existingPlaces, loadAvailablePlaces]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSearchQuery('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Filter places based on search query
  const filteredPlaces = availablePlaces.filter(place =>
    place.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected place name for display
  const selectedPlace = availablePlaces.find(place => place.id === toPlaceId);

  const handlePlaceSelect = (placeId: number) => {
    setToPlaceId(placeId);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleDropdownToggle = () => {
    if (!loading) {
      const willShow = !showDropdown;
      setShowDropdown(willShow);
      if (willShow && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      } else if (!willShow) {
        setSearchQuery('');
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!toPlaceId) {
      setError('Please select a place to connect to');
      return;
    }

    try {
      setError(null);
      const response = await memoryService.createConnection({
        user_id: userId,
        from_place_id: fromPlaceId,
        to_place_id: Number(toPlaceId),
        description: description.trim() || undefined
      });

      if (response.success) {
        onSave();
      } else {
        setError(response.message || 'Failed to create connection');
      }
    } catch (err) {
      console.error('Error creating connection:', err);
      setError('Failed to create connection');
    }
  };

  return (
    <div className="memory-palace-connection-form">
      <form onSubmit={handleSubmit} className="memory-palace-connection-form-content">
        <div className="memory-palace-form-field">
          <label htmlFor="connection-to">Connect To *</label>
          {loading ? (
            <div>Loading places...</div>
          ) : (
            <div className="memory-palace-searchable-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="memory-palace-searchable-dropdown-trigger"
                onClick={handleDropdownToggle}
                aria-expanded={showDropdown}
                aria-haspopup="listbox"
              >
                <span className="memory-palace-searchable-dropdown-value">
                  {selectedPlace ? selectedPlace.name : 'Select a place...'}
                </span>
                <span className="memory-palace-searchable-dropdown-arrow">
                  {showDropdown ? '▲' : '▼'}
                </span>
              </button>
              {showDropdown && (
                <div className="memory-palace-searchable-dropdown-menu">
                  <div className="memory-palace-searchable-dropdown-search">
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="memory-palace-searchable-dropdown-input"
                      placeholder="Search places..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="memory-palace-searchable-dropdown-list">
                    {filteredPlaces.length > 0 ? (
                      filteredPlaces.map((place) => (
                        <button
                          key={place.id}
                          type="button"
                          className={`memory-palace-searchable-dropdown-item ${
                            toPlaceId === place.id ? 'selected' : ''
                          }`}
                          onClick={() => handlePlaceSelect(place.id)}
                        >
                          {place.name}
                        </button>
                      ))
                    ) : (
                      <div className="memory-palace-searchable-dropdown-empty">
                        {searchQuery ? `No places found matching "${searchQuery}"` : 'No places available'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="memory-palace-form-field">
          <label htmlFor="connection-description">Connection Description</label>
          <textarea
            id="connection-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description for this connection"
            rows={3}
            className="memory-palace-form-textarea"
          />
        </div>

        {error && (
          <div className="memory-palace-form-error">{error}</div>
        )}

        <div className="memory-palace-form-actions">
          <button type="button" onClick={onCancel} className="memory-palace-form-btn cancel">
            Cancel
          </button>
          <button type="submit" className="memory-palace-form-btn save" disabled={!toPlaceId}>
            Create Connection
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConnectionForm;

